import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Recall } from "@/Common/Api/Communications/Dto/Recall";
import { RecallCommunication } from "@/Common/Api/Communications/Dto/RecallCommunication";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SingleSelectAutoComplete } from "@/Common/Components/Inputs/SingleSelectAutocomplete";
import { PatientBannerVertical } from "@/Common/Components/Patient/PatientBannerVertical";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientBannerHelper } from "@/Common/Helpers/PatientBannerHelper";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import { CommunicationStateType, RedirectTo } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";

interface IRecallView_V2Props {
    model: Recall;
    onResponseCallback: () => void;
    savedState: RecallView_V2State;
}

class RecallView_V2State {
    public model: Recall;
    public showClinicianPopupMenu: boolean;
    public priorities: FlatLookupView[];
    public patientModel: IPatientBannerModel;
    public statuses: FlatLookupView[];
    public note: string;
    public textDescription: string;
    public dueDate: Date;
    public selectedDescriptions: KeyValuePair<string, string>[];
    public selectedClinicians: Person[];
}

class RecallView_V2 extends BaseReactComponent<IRecallView_V2Props, RecallView_V2State> {
    constructor(props: IRecallView_V2Props) {
        super(props);

        let state = props.savedState;
        if (!state || state.model !== props.model) {
            state = new RecallView_V2State();
            state.priorities = CommunicationsHelper.GetRecallPrioritiesLookupsFromCache();
            state.model = MyleHelper.CloneObject(props.model);
            state.statuses = CommunicationsHelper.GetRecallStatusesLookupsFromCache();
            state.note = "";
            state.dueDate = LuxonDateTimeParser.parse(state.model.DueDate).toJSDate();
            state.textDescription = state.model.Type;
            state.selectedDescriptions = [];
            state.model.Status = state.model.RecallCommunications?.[0]?.StateLookupId;
            state.selectedClinicians = this.getSelectedClinicianPerson(state.model.DoctorContactId);
        }
        this.state = state;
    }

    public componentDidMount(): void {
        this.loadPatientBannerAsync();

        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        let cssClassColLg9or12 = this.state.patientModel ? "col-lg-8" : "col-lg-12";

        let creationDate = "";
        if (!IsNullOrWhiteSpace(this.state.model.CreationDate)) {
            creationDate = ` (${moment(this.state.model.CreationDate).toDate().format("YYYY-MM-DD HH:mm")})`;
        }
        const hasDescription = this.state.textDescription.trim().length > 0;
        const hasResponsibleProvider = !!this.state.model.DoctorContactId;
        const hasDueDate = !!this.state.dueDate;

        return (
            <div className="workspace">
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <p className="header-font-color">{cacheStore.GetString("CommunicationsLabel")}</p>
                            <h3 className="thin-font">{cacheStore.GetString("RecallLabel")}</h3>
                        </div>
                    </div>
                </div>

                <div className="fxheight hide-overflow">
                    <div className={cssClassColLg9or12}>
                        <div className="myle-doc-tb main-content myle-form scrollable documents">
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">{cacheStore.GetString("DescriptionLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    <SingleSelectAutoComplete
                                        id={this.state.model.Id}
                                        dataCy="recallView-description"
                                        valid={hasDescription}
                                        useblurevent
                                        componentId="recall_edit_description"
                                        selectedItems={this.state.selectedDescriptions.toImmutable()}
                                        displayedTextValue={this.state.textDescription}
                                        onSelecting={kvp => this.mutateState(s => s.textDescription = kvp.Value)}
                                        focusOnLoad={false}
                                        onChangeText={text => this.mutateState(s => s.textDescription = text)}
                                        closeDropDownAfterSelection
                                    />

                                </div>
                            </div>
                            <div className="row recall-provider-margin-button">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">
                                        {cacheStore.GetString("ResponsibleProviderLabel")}
                                    </label>
                                </div>
                                <div className="col-lg-6 col-md-9 col-sm-12 ">
                                    <PersonSearchAutocompleteCustom
                                        valid={this.state.selectedClinicians.length > 0}
                                        componentId="recall_clinician"
                                        dataCy="recallView-clinician"
                                        searchStrategy={new ClinicianSearchStrategy(cacheStore.CliniciansAndEmployeeGroups)}
                                        selectedItemList={this.state.selectedClinicians.toImmutable()}
                                        onSelectionCallback={personItem => this.selectClinician(personItem)}
                                        onClearCallback={personItem => this.removeClinician(personItem)}
                                        disabled={false}
                                        allowMultipleItemSelection={true}
                                        closeDropDownAfterSelection={true}
                                    />
                                </div>
                            </div>
                            <div className="row recall-provider-links-margin-left">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <a data-cy="recallView-newMessage" href="#" onClick={(e: React.MouseEvent) => this.onSendMessage(e)}>{cacheStore.GetString("SendMessageLabel")}</a>
                                    <span className="separator">|</span>
                                    <a href="#" onClick={(e: React.MouseEvent) => this.onSendTask(e)}>{cacheStore.GetString("SendTaskLabel")}</a>
                                    <span className="separator">|</span>
                                    <HyperLinkRedirectControl
                                        target={RedirectTo.Calendar}
                                        redirectValue={this.state.model?.DoctorContactId}
                                        text={cacheStore.GetString("ViewScheduleLabel")}
                                        targetPatient={this.state.patientModel?.get("Model").IndividualResponse.Contact_id}
                                        subject={this.state.model?.Type}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">{cacheStore.GetString("SpecialtyLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer" data-cy="recallView-speciality">
                                    <strong>{cacheStore.GetLocalizedValueByLookupId(this.state.model.SpecializationLookupId)}</strong>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">{cacheStore.GetString("DueDateLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    <DateSelector
                                        id="task_start_datey"
                                        cssClass=""
                                        date={this.state.dueDate}
                                        dateFormat="yyyy/MM/dd"
                                        disabled={false}
                                        toDayEnabled={true}
                                        onChangeCallback={date => this.mutateState(s => s.dueDate = date)}
                                        valid={hasDueDate}
                                        dataCy="recallView-dueDate"
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">{cacheStore.GetString("CreatedByLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    <strong>{cacheStore.GetCliniciansPersonsByContactId(this.state.model.CreatorContactId).Name + creationDate}</strong>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">{cacheStore.GetString("PriorityLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                                    <select
                                        onChange={(e: any) => this.handlePriorityChange(e.target.value)}
                                        className="form-control myle-control"
                                        data-cy="recallView-priority"
                                        value={this.state.model.PriorityLookupId}
                                    >
                                        {this.state.priorities.map(p => (
                                            <option key={p.LookupID} value={p.LookupID}> {cacheStore.GetLocalizedValueByLookupId(p.LookupID)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-12 text-right">
                                    <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-9 col-sm-12" data-cy="recallView-notes">
                                    <strong>{this.state.model.Notes}</strong>
                                </div>
                            </div>

                            {this.renderCommunicationHistory()}

                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                    <label className="form-label">{cacheStore.GetString("StateLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">

                                    <select
                                        onChange={(e: any) => this.handleStatusChange(e.target.value)}
                                        className="form-control myle-control"
                                        data-cy="recallView-state"
                                        value={this.state.model.Status}
                                    >
                                        <option key="state_empty" value="" />
                                        {this.state.statuses.map(s => (
                                            <option key={s.LookupID} value={s.LookupID}> {cacheStore.GetLocalizedValueByLookupId(s.LookupID)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-3 col-md-3 col-sm-12 text-right">
                                    <label className="form-label">{cacheStore.GetString("AddNoteLabel")}</label>
                                </div>
                                <div className="col-lg-6 col-md-9 col-sm-12">
                                    <textarea
                                        className="form-control"
                                        onChange={(e: any) => this.handleNoteChange(e.target.value)}
                                        id="recall_view_recallnote"
                                        data-cy="recallView-addNote"
                                        value={this.state.note}
                                        disabled={false}
                                        rows={4}
                                    />
                                </div>
                            </div>

                        </div>
                    </div>
                    {this.renderPatientBanner()}
                </div>
                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 text-right">
                            <button
                                className="btn btn-default push-right-5"
                                onClick={() => this.handleCancel()}
                            >
                                {cacheStore.GetString("CancelLabel")}
                            </button>
                            <button
                                disabled={!hasDescription || !hasResponsibleProvider || !hasDueDate}
                                className="btn btn-primary push-right-5"
                                data-cy="recallView-save"
                                onClick={() => this.saveRecall(false)}
                            >
                                {cacheStore.GetString("SaveButton")}
                            </button>
                            {!this.state.model.IsCompleted && (
                                <button
                                    disabled={!hasDescription || !hasResponsibleProvider}
                                    className="btn btn-primary"
                                    data-cy="recallView-markRecallAsCompleted"
                                    onClick={() => this.saveRecall(true)}
                                >
                                    {cacheStore.GetString("MarkRecallAsCompletedLabel")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public componentWillUnmount(): void {
        setTimeout(() => sessionStore.dispatch(
            LocalOperations.Communications.CommunicationStatesSave,
            {
                type: CommunicationStateType.RecallView,
                state: this.state
            }
        ), 0);
    }

    private async loadPatientBannerAsync(): Promise<void> {
        await PatientBannerHelper.loadPatientBannerModel(
            this.props.model.PatientLinkContactId,
            model => this.mutateState(state => state.patientModel = model),
            true
        );
    }

    private handlePriorityChange(id: string): void {
        this.mutateState(s => s.model.PriorityLookupId = id);
    }

    private handleStatusChange(id: string): void {
        this.mutateState(s => s.model.Status = id);
    }

    private handleNoteChange(text: string): void {
        this.mutateState(s => s.note = text);
    }

    private handleCancel(): void {
        this.mutateState(
            s => {
                s.model = this.props.model;
                s.note = "";
            },
            () => this.props.onResponseCallback()
        );
    }

    private saveRecall(setRecallAsComplete: boolean): void {
        const recallCom = new RecallCommunication();
        recallCom.Notes = this.state.note;
        recallCom.StateLookupId = this.state.model.Status;
        recallCom.RecallId = this.state.model.Id;
        let recall = this.state.model;
        recall.Type = this.state.textDescription;
        recall.DueDate = (this.state.dueDate == null) ? null : this.state.dueDate.format("YYYY-MM-DD HH:mm:ss");
        recall.DoctorContactId = this.state.selectedClinicians.firstOrDefault().ContactId;

        Client.Postv2(
            ServerOperations.Server.Myle_SaveRecallCommunication,
            {
                Recall: recall,
                RecallCommunication: recallCom,
                SetRecallToComplete: setRecallAsComplete,
                ReturnSavedObject: true
            },
            response => {
                if (response) {
                    this.props.onResponseCallback();
                }
            }
        );
    }

    private renderPatientBanner(): JSX.Element {
        if (!this.state.patientModel) {
            return (
                <div className="left-border col-lg-4 patient-view-panel-right patient-banner-form-wrapper">
                    <div className="myle-form scrollable" />
                </div>
            );
        }

        return (
            <div className="left-border col-lg-4 patient-view-panel-right patient-banner-form-wrapper">
                <div className="myle-form scrollable">
                    {this.state.patientModel != null && (
                        <PatientBannerVertical
                            model={this.state.patientModel}
                            recallCompleteFunction={recallId => this.completeRecall(recallId)}
                            dataCy="recallView-patientName"
                        />
                    )}
                </div>
            </div>
        );
    }

    private completeRecall(recallId: string): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetRecallToCompleted,
            { RecallId: recallId },
            async () => {
                // refresh to search view if current recall is completed
                if (recallId == this.props.model.Id)
                    this.props.onResponseCallback && this.props.onResponseCallback();

                await PatientBannerHelper.loadPatientBannerModel(
                    this.props.model.PatientLinkContactId,
                    model => {
                        this.mutateState(state => state.patientModel = model);
                    },
                    true
                );
            }
        );
    }

    private renderCommunicationHistory(): JSX.Element {
        if (!this.state.model.RecallCommunications)
            return null;

        const communications = this.state.model.RecallCommunications;
        let communicationElements = communications.map(x => {
            let noteLinesJsx: JSX.Element[] = null;

            if (x.Notes && x.Notes.length > 0) {
                const noteLines = x.Notes.split("\n");

                noteLinesJsx = noteLines.map((x, idx) =>
                    <div key={`note${idx}`}><span>{x}</span><br /></div>
                );
            }

            return (
                <div key={x.Id + "__notes"} style={{ marginBottom: "15px" }}>
                    <div>
                        <strong>
                            <span>{cacheStore.GetCliniciansPersonsByContactId(x.ContactId).Name}</span>
                            <span className="separator">{cacheStore.GetString("OnLabel")}</span>
                            <span>{moment(x.DateCreated).format("YYYY-MM-DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(x.DateCreated).format("hh:mm a")}</span>
                            {x.StateLookupId && <span className="separator">|</span>}
                            <span className="logo-color">{cacheStore.GetLocalizedValueByLookupId(x.StateLookupId)}</span>
                        </strong>
                    </div>
                    <div style={{ marginTop: "5px" }}>
                        {noteLinesJsx}
                    </div>
                </div>
            );
        });

        return (
            <div className="row">
                <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                    <label className="form-label">{cacheStore.GetString("CommunicationHistoryLabel")}</label>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 form-field-answer">
                    {communicationElements}
                </div>
            </div>
        );
    }

    private selectClinician(personItem: PersonDropdownItem): void {
        if (!personItem?.Item)
            return;

        if (!this.state.selectedClinicians.any(p => p.ContactId === personItem.Item.ContactId)) {
            this.mutateState(s => {
                s.selectedClinicians = [personItem.Item];
                s.model.DoctorContactId = personItem.Item.ContactId;
            });
        }
    }

    private removeClinician(personItem: PersonDropdownItem): void {
        if (!personItem || !personItem.Item)
            return;

        this.mutateState(s => {
            s.selectedClinicians = s.selectedClinicians.filter(x => x.ContactId != personItem.Item.ContactId);
            s.model.DoctorContactId = null;
        });
    }

    private getSelectedClinicianPerson(contactId: string): Person[] {
        return [cacheStore.GetCliniciansPersonsByContactId(contactId)] ?? [];
    }
    private onSendMessage(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();

        sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
            isTask: false,
            patient: this.getPersonFromPatientModel()
        });
    }

    private onSendTask(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();

        sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
            isTask: true,
            patient: this.getPersonFromPatientModel()
        });
    }

    private getPersonFromPatientModel(): Person {
        const patientModel = this.state.patientModel.get("Model");
        return Person.FromPatient(patientModel);
    }
}

export { RecallView_V2, RecallView_V2State };
